import { html, attr, on, Attr, Fragment } from '@tempots/dom'
import type { TextBlock, WatermarkBlock } from '@rh/shared/schemas/design'
import type { DesignEditorState } from './design-state'
import type { DesignActions } from './design-actions'
import { chipHtml, chipsToTokens, tokensToChips } from './token-chips'
import { disposeChipMenu, openChipMenu } from './token-chip-menu'
import { TOKEN_FIELDS } from './token-fields'

type EditableBlock = TextBlock | WatermarkBlock

// ============================================================
// Rich-text editing for text/watermark blocks, ported from the
// sandbox prototype: a contenteditable overlay positioned over
// the block plus a floating formatting toolbar below it. Both
// are rendered *inside* the page canvas so their absolute
// positions are relative to the page, exactly as in the sandbox.
//
// Formatting uses document.execCommand — deprecated but
// sufficient for this narrow, server-side-sanitized allowlist.
// ============================================================

// ============================================================
// TOOLBAR STATE
// selectionchange → update toolbar to reflect current selection.
// ============================================================

document.addEventListener('selectionchange', () => {
  const overlay = document.querySelector<HTMLElement>('.editing-overlay')
  if (overlay && document.activeElement === overlay) updateToolbarState()
})

// ============================================================
// CUSTOM DROPDOWN (keeps focus in contenteditable via mousedown+preventDefault)
// ============================================================

let activeDropdownList: HTMLElement | null = null
let _toolbarSavedRange: Range | null = null

// While a toolbar input holds focus, the browser moves the document
// selection into it and the highlighted text goes visually blank — even
// though the saved range will still receive the change. Paint the saved
// range with a CSS custom highlight (styled in customize.css) so the user
// can see what their typed value will affect. No-op where unsupported.
function setPreservedHighlight(range: Range | null) {
  const w = window as unknown as {
    Highlight?: new (...ranges: Range[]) => unknown
    CSS?: { highlights?: Map<string, unknown> }
  }
  const highlights = w.CSS?.highlights
  if (!highlights || !w.Highlight) return
  if (range && !range.collapsed) {
    highlights.set('rh-preserved-selection', new w.Highlight(range))
  } else {
    highlights.delete('rh-preserved-selection')
  }
}

document.addEventListener('mousedown', (e: MouseEvent) => {
  if (!activeDropdownList) return
  const wrapper = activeDropdownList.parentElement
  if (wrapper && !wrapper.contains(e.target as Node)) {
    activeDropdownList.style.display = 'none'
    activeDropdownList = null
  }
})

// ============================================================
// CHIP FORMATTING
// execCommand never touches contenteditable=false chips, so the inline
// formatting commands (fontSize/fontName/foreColor) silently skip them.
// Each command therefore also styles the chips its selection covered:
// capture them BEFORE the command runs (it rewrites the DOM around
// them), then land the equivalent markup on a wrapper element — the
// same font/span + style vocabulary execCommand emits, which the
// sanitizer allowlists and chipsToTokens leaves in place on commit.
// ============================================================

function chipsInSelection(): HTMLElement[] {
  const overlay = document.querySelector<HTMLElement>('.editing-overlay')
  const sel = window.getSelection()
  if (!overlay || !sel || sel.rangeCount === 0) return []
  const range = sel.getRangeAt(0)
  if (range.collapsed) return []
  return Array.from(
    overlay.querySelectorAll<HTMLElement>('[data-token]')
  ).filter(chip => range.intersectsNode(chip))
}

/**
 * The element chip formatting lands on: an existing wrapper whose sole
 * content is the chip (repeated changes restyle instead of nesting), or
 * a fresh <font> created around it.
 */
function ensureChipWrapper(chip: HTMLElement): HTMLElement {
  const parent = chip.parentElement
  if (
    parent &&
    !parent.classList.contains('editing-overlay') &&
    (parent.tagName === 'FONT' || parent.tagName === 'SPAN') &&
    parent.childNodes.length === 1
  ) {
    return parent
  }
  const wrapper = document.createElement('font')
  chip.replaceWith(wrapper)
  wrapper.appendChild(chip)
  return wrapper
}

type ToggleCmd = 'bold' | 'italic' | 'underline' | 'strikeThrough'

const TOGGLE_CMDS = new Set<string>([
  'bold',
  'italic',
  'underline',
  'strikeThrough',
])

/**
 * A chip's effective toggle state: formatting tags/styles anywhere on
 * its ancestor chain inside the overlay (an imported whole-line <b>
 * bolds the chip just as our own wrapper style does).
 */
function chipToggleState(chip: HTMLElement, cmd: ToggleCmd): boolean {
  for (
    let el: HTMLElement | null = chip;
    el && !el.classList.contains('editing-overlay');
    el = el.parentElement
  ) {
    const s = el.style
    switch (cmd) {
      case 'bold':
        if (el.tagName === 'B' || el.tagName === 'STRONG') return true
        if (s.fontWeight)
          return s.fontWeight === 'bold' || parseInt(s.fontWeight, 10) >= 600
        break
      case 'italic':
        if (el.tagName === 'I' || el.tagName === 'EM') return true
        if (s.fontStyle) return s.fontStyle === 'italic'
        break
      case 'underline':
        if (el.tagName === 'U' || s.textDecoration.includes('underline'))
          return true
        break
      case 'strikeThrough':
        if (
          el.tagName === 'S' ||
          el.tagName === 'STRIKE' ||
          s.textDecoration.includes('line-through')
        )
          return true
        break
    }
  }
  return false
}

/** Add/remove one value in a wrapper's text-decoration list. */
function setWrapperDecoration(wrapper: HTMLElement, deco: string, on: boolean) {
  const parts = new Set(
    (wrapper.style.textDecoration || '').split(/\s+/).filter(Boolean)
  )
  if (on) parts.add(deco)
  else parts.delete(deco)
  wrapper.style.textDecoration = Array.from(parts).join(' ')
}

/**
 * Force a chip to a toggle state via wrapper styles. The explicit "off"
 * values (font-weight: normal etc.) matter: they override formatting
 * inherited from an ancestor tag the chip can't be extracted from.
 */
function setChipToggle(chip: HTMLElement, cmd: ToggleCmd, on: boolean) {
  if (chipToggleState(chip, cmd) === on) return
  const wrapper = ensureChipWrapper(chip)
  switch (cmd) {
    case 'bold':
      wrapper.style.fontWeight = on ? 'bold' : 'normal'
      break
    case 'italic':
      wrapper.style.fontStyle = on ? 'italic' : 'normal'
      break
    case 'underline':
      setWrapperDecoration(wrapper, 'underline', on)
      break
    case 'strikeThrough':
      setWrapperDecoration(wrapper, 'line-through', on)
      break
  }
}

/** Non-whitespace text in the selection OUTSIDE any chip? */
function selectionHasEditableText(): boolean {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return false
  const frag = sel.getRangeAt(0).cloneContents()
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT)
  let node: Node | null
  while ((node = walker.nextNode())) {
    const text = node as Text
    if (
      !text.parentElement?.closest('[data-token]') &&
      (text.nodeValue ?? '').trim()
    ) {
      return true
    }
  }
  return false
}

/**
 * Runs a toggle command, mirroring it onto covered chips with standard
 * toggle semantics: anything unformatted in the selection → everything
 * turns ON; only when text and chips are all formatted does it turn OFF.
 */
function execToggleWithChips(cmd: ToggleCmd) {
  const chips = chipsInSelection()
  const chipsOn =
    chips.length > 0 && chips.every(chip => chipToggleState(chip, cmd))
  const textOn = document.queryCommandState(cmd)
  const target = !(chipsOn && (textOn || !selectionHasEditableText()))
  document.execCommand(cmd)
  for (const chip of chips) setChipToggle(chip, cmd, target)
}

// ============================================================
// COLOR PICKER STATE + HELPERS
// ============================================================

let _cpHue = 0 // 0–360
let _cpSat = 100 // 0–100 (x in sat/val square)
let _cpVal = 50 // 0–100 (y in sat/val square, top = high val)
let _cpOpacity = 100 // 0–100 (100 = opaque)
let _cpDragging: 'hue' | 'sq' | null = null
let _cpSavedRange: Range | null = null

/** Current picker colour: plain hex when opaque, rgba() otherwise. */
function cpCurrentColor(): string {
  const hex = hsvToHex(_cpHue, _cpSat, _cpVal)
  if (_cpOpacity >= 100) return hex
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const a = Math.max(0, Math.min(1, _cpOpacity / 100))
  return `rgba(${r},${g},${b},${parseFloat(a.toFixed(2))})`
}

document.addEventListener('mousemove', (e: MouseEvent) => {
  if (!_cpDragging) return
  if (_cpDragging === 'hue') {
    const bar = document.querySelector<HTMLElement>('.toolbar-cp-hue')
    if (bar) {
      const r = bar.getBoundingClientRect()
      _cpHue = Math.max(
        0,
        Math.min(360, ((e.clientX - r.left) / r.width) * 360)
      )
      cpSyncUI()
      cpApplyColor()
    }
  } else {
    const sq = document.querySelector<HTMLElement>('.toolbar-cp-sq')
    if (sq) {
      const r = sq.getBoundingClientRect()
      _cpSat = Math.max(
        0,
        Math.min(100, ((e.clientX - r.left) / r.width) * 100)
      )
      _cpVal = Math.max(
        0,
        Math.min(100, 100 - ((e.clientY - r.top) / r.height) * 100)
      )
      cpSyncUI()
      cpApplyColor()
    }
  }
})

document.addEventListener('mouseup', () => {
  if (_cpDragging) _cpDragging = null
})

function hsvToHex(h: number, s: number, v: number): string {
  s /= 100
  v /= 100
  const f = (n: number) => {
    const k = (n + h / 60) % 6
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1))
  }
  const hex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${hex(f(5))}${hex(f(3))}${hex(f(1))}`
}

function hexToHsv(raw: string): { h: number; s: number; v: number } {
  let hex = raw.replace('#', '')
  if (hex.length === 3)
    hex = hex
      .split('')
      .map(c => c + c)
      .join('')
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    d = max - min
  let h = 0
  if (d > 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60
    else if (max === g) h = ((b - r) / d + 2) * 60
    else h = ((r - g) / d + 4) * 60
  }
  return { h, s: max === 0 ? 0 : (d / max) * 100, v: max * 100 }
}

function cpApplyColor() {
  const color = cpCurrentColor()
  const overlay = document.querySelector<HTMLElement>('.editing-overlay')
  if (!overlay) return
  if (document.activeElement !== overlay) {
    overlay.focus()
    if (_cpSavedRange) {
      try {
        const s = window.getSelection()
        if (s) {
          s.removeAllRanges()
          s.addRange(_cpSavedRange)
        }
      } catch {
        /* stale range */
      }
    }
  }
  const chips = chipsInSelection()
  if (color.startsWith('rgba')) {
    // A <font color> attribute can't express alpha; styleWithCSS makes
    // foreColor emit <span style="color: rgba(...)"> instead (the sanitizer
    // allowlists the color style property). Scoped tightly so other
    // execCommands (bold/italic) keep producing their tag markup, which is
    // what the sanitizer expects.
    document.execCommand('styleWithCSS', false, 'true')
    document.execCommand('foreColor', false, color)
    document.execCommand('styleWithCSS', false, 'false')
  } else {
    document.execCommand('foreColor', false, color)
  }
  for (const chip of chips) {
    ensureChipWrapper(chip).style.color = color
  }
  // Re-save range so repeated Apply calls still work
  const sel = window.getSelection()
  if (sel && sel.rangeCount > 0) _cpSavedRange = sel.getRangeAt(0).cloneRange()
}

function cpSyncUI() {
  const hex = hsvToHex(_cpHue, _cpSat, _cpVal)
  const color = cpCurrentColor()

  // Hex input (don't overwrite while user is typing in it)
  const hexInput = document.querySelector<HTMLInputElement>('.toolbar-cp-hex')
  if (hexInput && document.activeElement !== hexInput)
    hexInput.value = hex.slice(1).toUpperCase()

  // Preview swatch
  const preview = document.querySelector<HTMLElement>('.toolbar-cp-preview')
  if (preview) preview.style.background = color

  // Trigger indicator
  const ind = document.querySelector<HTMLElement>('.toolbar-color-indicator')
  if (ind) ind.style.background = color

  // Opacity slider + editable percentage field (don't overwrite while typing)
  const op = document.querySelector<HTMLInputElement>('.toolbar-cp-opacity')
  if (op && document.activeElement !== op)
    op.value = String(Math.round(_cpOpacity))
  const opLabel = document.querySelector<HTMLInputElement>(
    '.toolbar-cp-opacity-label'
  )
  if (opLabel && document.activeElement !== opLabel)
    opLabel.value = String(Math.round(_cpOpacity))

  // Square gradient (only hue-dependent part needs updating)
  const sq = document.querySelector<HTMLElement>('.toolbar-cp-sq')
  if (sq)
    sq.style.background =
      'linear-gradient(to bottom, rgba(0,0,0,0), #000),' +
      `linear-gradient(to right, #fff, hsl(${_cpHue},100%,50%))`

  // Hue thumb
  const hueTh = document.querySelector<HTMLElement>('.toolbar-cp-hue-thumb')
  if (hueTh) hueTh.style.left = `${(_cpHue / 360) * 100}%`

  // Sat/val thumb
  const sqTh = document.querySelector<HTMLElement>('.toolbar-cp-sq-thumb')
  if (sqTh) {
    sqTh.style.left = `${_cpSat}%`
    sqTh.style.top = `${100 - _cpVal}%`
  }
}

// ============================================================
// OVERLAY HELPERS
// ============================================================

/**
 * Vertical gap between a component and floating panels placed *above or
 * beside* it — clears the rotate handle (28px line + 12px knob).
 * Panels placed below sit flush against the component border.
 */
export const PANEL_CLEARANCE = 44

/**
 * Keeps a floating toolbar fully on-page. Takes the mounted element (not
 * a selector): the controls stack is rebuilt on unrelated state ticks,
 * and a selector lookup inside the rAF could position a stale node while
 * the fresh one kept its unclamped inline position.
 *
 * Horizontally it shifts the
 * toolbar left as needed so it never extends past the page's right edge
 * (a component near the right edge used to push the toolbar into the
 * scroller's overflow, off-screen). Vertically it keeps the existing
 * behavior: below the component when it fits (e.g. a footer flips the
 * toolbar above the component instead) — and as a last resort (very tall
 * panel, component near the top) clamps it inside the page, which is
 * always scrollable into view.
 */
export function keepToolbarOnPage(
  el: HTMLElement,
  comp: { x: number; y: number; height: number },
  pageWidth: number,
  pageHeight: number
) {
  requestAnimationFrame(() => {
    if (!el.isConnected) return
    const w = el.offsetWidth
    el.style.left = `${Math.max(0, Math.min(comp.x, pageWidth - w))}px`
    const h = el.offsetHeight
    const below = comp.y + comp.height
    if (below + h <= pageHeight) return
    // PANEL_CLEARANCE keeps the rotate handle (stem + knob above the
    // component) grabbable — the handles layer renders below the controls'
    // z-index, so a flush panel would bury it.
    const above = comp.y - h - PANEL_CLEARANCE
    el.style.top = `${above >= 0 ? above : Math.max(0, pageHeight - h)}px`
  })
}

/**
 * Positions a floating panel so it never covers its component's resize
 * handles: flush below the component, else above, else beside it (right,
 * then left), each fully on-page. Only when the component spans essentially
 * the whole page (no clear side exists) does it fall back to clamping the
 * panel on-page over the component — overlap is unavoidable there. Runs
 * after mount because the panel's size varies with its content.
 */
export function placePanelClearOfComponent(
  el: HTMLElement,
  comp: { x: number; y: number; width: number; height: number },
  pageWidth: number,
  pageHeight: number,
  margin = PANEL_CLEARANCE
) {
  requestAnimationFrame(() => {
    if (!el.isConnected) return
    const w = el.offsetWidth
    const h = el.offsetHeight
    const clampX = (x: number) => Math.max(0, Math.min(x, pageWidth - w))
    const clampY = (y: number) => Math.max(0, Math.min(y, pageHeight - h))
    const place = (left: number, top: number) => {
      el.style.left = `${left}px`
      el.style.top = `${top}px`
    }

    const below = comp.y + comp.height
    if (below + h <= pageHeight) return place(clampX(comp.x), below)

    const above = comp.y - h - margin
    if (above >= 0) return place(clampX(comp.x), above)

    const right = comp.x + comp.width + margin
    if (right + w <= pageWidth) return place(right, clampY(comp.y))

    const left = comp.x - w - margin
    if (left >= 0) return place(left, clampY(comp.y))

    place(clampX(comp.x), clampY(pageHeight - h))
  })
}

/**
 * Repositions the floating chrome (element-controls stack, resize-handle
 * wrapper, south resize handles) to the editing overlay's current rendered
 * height, clamped to the block's stored height. Unlike a grow-only move
 * this also brings the chrome back up — needed because applyFontSize goes
 * through a transient execCommand('fontSize', 7) that inflates the overlay
 * for one synchronous input event before the real pixel size is written
 * back.
 */
function syncOverlayChrome(comp: EditableBlock) {
  const overlay = document.querySelector<HTMLElement>('.editing-overlay')
  if (!overlay) return
  const actualH = Math.max(overlay.offsetHeight, comp.height)

  // Only when the controls stack sits below the component; when it was
  // flipped above (see keepToolbarOnPage) height changes don't affect it.
  const controls = document.querySelector<HTMLElement>('.element-controls')
  if (controls && parseFloat(controls.style.top) > comp.y) {
    controls.style.top = `${comp.y + actualH}px`
  }

  const wrapper = document.querySelector<HTMLElement>('.handles-wrapper')
  if (wrapper) wrapper.style.height = `${actualH}px`

  wrapper?.querySelectorAll<HTMLElement>('[style*="cursor"]').forEach(h => {
    const cursor = h.style.cursor
    if (
      cursor === 's-resize' ||
      cursor === 'se-resize' ||
      cursor === 'sw-resize'
    ) {
      h.style.top = `${actualH - 3.5}px`
    }
  })
}

// Set overlay innerHTML and focus with cursor at end
export function initOverlay(content: string) {
  const overlay = document.querySelector<HTMLElement>('.editing-overlay')
  if (!overlay) return
  overlay.innerHTML = tokensToChips(content)
  overlay.focus()
  const range = document.createRange()
  range.selectNodeContents(overlay)
  range.collapse(false)
  window.getSelection()?.removeAllRanges()
  window.getSelection()?.addRange(range)
  requestAnimationFrame(() => updateToolbarState())
}

// ============================================================
// EDITING OVERLAY
// ============================================================

/**
 * True when a CSS color (hex or rgb/rgba) is light enough that it would
 * be unreadable on the overlay's standard light editing surface.
 */
function isLightColor(color: string): boolean {
  let r: number
  let g: number
  let b: number
  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1]
  if (hex) {
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map(c => c + c)
            .join('')
        : hex
    r = parseInt(full.slice(0, 2), 16)
    g = parseInt(full.slice(2, 4), 16)
    b = parseInt(full.slice(4, 6), 16)
  } else {
    const m = color.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i)
    if (!m) return false
    r = +m[1]
    g = +m[2]
    b = +m[3]
  }
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.65
}

/**
 * Whether the overlay needs the dark editing surface: the block's base
 * color is light, or every color applied inside the content is (toolbar
 * recolors wrap spans without touching comp.color). Mixed light/dark
 * content has no right answer — the base color wins there.
 */
function overlayNeedsDarkSurface(comp: EditableBlock): boolean {
  if (isLightColor(comp.color)) return true
  const decls = [
    ...comp.content.matchAll(/color=["']([^"']+)["']/gi),
    ...comp.content.matchAll(/(?<!-)color:\s*([^;"']+)/gi),
  ].map(m => m[1].trim())
  return decls.length > 0 && decls.every(isLightColor)
}

export function renderEditingOverlay(
  comp: EditableBlock,
  state: DesignEditorState,
  actions: DesignActions
) {
  // The overlay can render taller than the block's stored height (content
  // that wraps past the last committed size, plus the overlay's own padding
  // and border). Re-sync the chrome once it has laid out so the controls
  // below never cover the last line of text.
  requestAnimationFrame(() => syncOverlayChrome(comp))

  const style = [
    'position: absolute;',
    `left: ${comp.x}px; top: ${comp.y}px;`,
    `width: ${comp.width}px; min-height: ${comp.height}px;`,
    `font-size: ${comp.fontSize}px;`,
    `font-family: ${comp.fontFamily};`,
    `text-align: ${comp.textAlign};`,
    `color: ${comp.color};`,
    'border: 2px solid #3b82f6;',
    // The editing surface must contrast with the block's own text color —
    // white text (dark banners) gets a dark slate surface, dark text the
    // standard light one. Chips inherit currentColor, so they follow.
    `background: ${overlayNeedsDarkSurface(comp) ? 'rgba(15,23,42,0.92)' : 'rgba(239,246,255,0.92)'};`,
    'padding: 4px 6px; box-sizing: border-box;',
    `z-index: 10; outline: none; line-height: ${comp.lineHeight};`,
    'word-wrap: break-word; overflow: hidden;',
    comp.rotation
      ? `transform: rotate(${comp.rotation}deg); transform-origin: center;`
      : '',
  ].join(' ')

  return html.div(
    attr.class('editing-overlay'),
    attr.style(style),
    attr.contenteditable('true'),
    // Seed content at render time: any page re-render re-creates this
    // overlay (e.g. a z-order move notifies the selectedComponent signal
    // without changing its value, so only the DOM is rebuilt), and an
    // empty contenteditable would make the text visually vanish. While
    // editing, content lives in the DOM only and is committed on blur.
    attr.innerHTML(tokensToChips(comp.content)),
    // Chip gestures: the ▾ arrow opens the fill-ins menu (swap / remove);
    // a plain click on the chip body SELECTS the chip so the formatting
    // toolbar can act on it. A drag that ends on a chip leaves its
    // non-collapsed selection untouched.
    on.click((e: MouseEvent) => {
      e.stopPropagation()
      const target = e.target as HTMLElement
      const chip = target.closest?.('[data-token]') as HTMLElement | null
      if (!chip) return
      e.preventDefault()
      if (target.closest('.rh-chip-arrow')) {
        openChipMenu(chip, e.currentTarget as HTMLElement)
        return
      }
      const sel = window.getSelection()
      if (sel && sel.isCollapsed) {
        const range = document.createRange()
        range.selectNode(chip)
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }),
    on.blur((e: FocusEvent) => {
      disposeChipMenu()
      // Focus moving into the floating controls (the size/line-height
      // inputs, color-picker fields) is part of the same editing
      // interaction: committing here would re-render the overlay and
      // detach the selection those controls are about to act on. The
      // content still commits on a real blur (deselect / click-away),
      // because those controls hand focus back to the overlay when done.
      const next = e.relatedTarget as HTMLElement | null
      if (next?.closest('.element-controls')) return
      // Commit against the LIVE component, not the render-time `comp`:
      // this blur also fires when a toolbar commit (e.g. a line-height
      // change) re-renders the overlay out from under the focus, and
      // spreading a stale comp here would overwrite that fresh commit
      // with old values.
      const live = state.components.value.find(c => c.id === comp.id)
      if (!live || (live.type !== 'text' && live.type !== 'watermark')) return
      const el = e.target as HTMLElement
      const content = chipsToTokens(el.innerHTML)
      const newHeight = Math.max(live.height, el.offsetHeight)
      if (content !== live.content || newHeight !== live.height) {
        actions.updateComponent({ ...live, content, height: newHeight }, true)
      }
    }),
    on.keydown((e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const el = e.target as HTMLElement
        el.innerHTML = tokensToChips(comp.content)
        el.blur()
        state.selectedId.set(null)
      }
    }),
    // Reposition all controls when the overlay's rendered height changes
    // (text wrapping growth, deletions, formatting commands).
    on.input(() => syncOverlayChrome(comp))
  )
}

// ============================================================
// FORMATTING TOOLBAR
// ============================================================

// Read the selection's formatting and push it into the toolbar DOM imperatively.
// Called on every selectionchange while the overlay has focus.
function updateToolbarState() {
  const overlay = document.querySelector<HTMLElement>('.editing-overlay')
  if (!overlay || document.activeElement !== overlay) return

  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return

  // Get computed style at the selection's start node
  let node: Node = sel.getRangeAt(0).startContainer
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement!
  if (!overlay.contains(node)) return
  const style = window.getComputedStyle(node as HTMLElement)

  // The size/line-height labels are <input> elements (editable dropdowns) —
  // their displayed text is the value property, not textContent. Leave them
  // alone while the user is typing in them.
  const setLabel = (el: HTMLElement | null, text: string) => {
    if (!el || document.activeElement === el) return
    if (el instanceof HTMLInputElement) el.value = text
    else el.textContent = text
  }

  // Font size — reflect the selection's actual rendered size
  setLabel(
    document.querySelector<HTMLElement>('.toolbar-size-label'),
    `${Math.round(parseFloat(style.fontSize))}px`
  )

  // Font family — match first family name and update label
  const fontLabelEl = document.querySelector<HTMLElement>('.toolbar-font-label')
  if (fontLabelEl) {
    const first = style.fontFamily
      .split(',')[0]
      .trim()
      .replace(/['"]/g, '')
      .toLowerCase()
    const match = FONT_OPTIONS.find(opt => {
      const optFirst = opt.value
        .split(',')[0]
        .trim()
        .replace(/['"]/g, '')
        .toLowerCase()
      return optFirst === first
    })
    if (match) fontLabelEl.textContent = match.label
  }

  // Button active states. queryCommandState only sees editable text, so
  // when the selection is a chip (or the caret sits inside one), read the
  // toggle states off the chip's ancestor chain instead; alignment comes
  // from computed style, which is editability-blind.
  const range = sel.getRangeAt(0)
  let chipEl = (node as HTMLElement).closest?.(
    '[data-token]'
  ) as HTMLElement | null
  if (
    !chipEl &&
    !range.collapsed &&
    range.startContainer === range.endContainer
  ) {
    const child = range.startContainer.childNodes[range.startOffset]
    if (
      range.endOffset - range.startOffset === 1 &&
      child instanceof HTMLElement &&
      child.hasAttribute('data-token')
    ) {
      chipEl = child
    }
  }
  const toggleState = (cmd: ToggleCmd) =>
    chipEl ? chipToggleState(chipEl, cmd) : document.queryCommandState(cmd)
  const align =
    style.textAlign === 'center'
      ? 'center'
      : style.textAlign === 'right' || style.textAlign === 'end'
        ? 'right'
        : 'left'
  const states: Record<string, boolean> = {
    'toolbar-btn-bold': toggleState('bold'),
    'toolbar-btn-italic': toggleState('italic'),
    'toolbar-btn-underline': toggleState('underline'),
    'toolbar-btn-strike': toggleState('strikeThrough'),
    'toolbar-btn-align-left': align === 'left',
    'toolbar-btn-align-center': align === 'center',
    'toolbar-btn-align-right': align === 'right',
  }
  Object.entries(states).forEach(([cls, active]) => {
    const el = document.querySelector<HTMLElement>(`.${cls}`)
    if (!el) return
    el.style.background = active ? '#3b82f6' : 'transparent'
    el.style.color = active ? '#fff' : '#e2e8f0'
  })

  // Line height — block-level, read from overlay's inline style
  if (overlay.style.lineHeight) {
    setLabel(
      document.querySelector<HTMLElement>('.toolbar-lh-label'),
      overlay.style.lineHeight
    )
  }

  // Text color indicator
  const colorIndicatorEl = document.querySelector<HTMLElement>(
    '.toolbar-color-indicator'
  )
  if (colorIndicatorEl) colorIndicatorEl.style.background = style.color
}

// Exported for the fonts-coverage test: every entry must stay renderable
// by the html-renderer's font registry (see fontFamilyAliases).
export const FONT_OPTIONS = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  { label: 'Courier New', value: "'Courier New', monospace" },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Trebuchet MS', value: "'Trebuchet MS', sans-serif" },
  { label: 'Palatino', value: "'Palatino Linotype', serif" },
  { label: 'Garamond', value: 'Garamond, serif' },
]

const FONT_SIZES = [
  8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72,
]
const LINE_HEIGHTS = [
  '0.4',
  '0.45',
  '0.5',
  '0.55',
  '0.6',
  '0.65',
  '0.7',
  '0.75',
  '0.8',
  '0.85',
  '0.9',
  '0.95',
  '1',
  '1.1',
  '1.15',
  '1.25',
  '1.5',
  '1.75',
  '2',
  '2.5',
  '3',
]

// Dynamic-field tokens (resolved to live recruiter/agency data at render
// time, see design-tokens.ts). Labels come from the shared field list so
// the menu, the canvas chips, and the wizard all agree.
const INSERT_FIELD_OPTIONS: Array<{ label: string; value: string }> =
  TOKEN_FIELDS.map(f => ({ label: f.label, value: f.token }))

// Custom dropdown: all interactions via mousedown+preventDefault so focus
// never leaves the contenteditable overlay.
function ToolbarDropdown(opts: {
  wrapCls: string
  labelCls: string
  listCls: string
  initialLabel: string
  width: string
  btnBg?: string
  options: Array<{ label: string; value: string }>
  onSelect: (value: string) => void
  editable?: boolean
  onType?: (value: string) => void
  keepLabel?: boolean
}) {
  const bg = opts.btnBg ?? '#334155'
  // Only a value the user actually typed may be applied on blur. The input
  // also blurs when an option pick refocuses the overlay — auto-applying
  // its stale value there would silently overwrite the freshly picked one.
  let inputDirty = false
  const triggerStyle = [
    `height: 34px; width: ${opts.width}; border: none; border-radius: 4px; cursor: pointer;`,
    `background: ${bg}; color: #e2e8f0; font-size: 15px;`,
    'display: inline-flex; align-items: center; padding: 0 6px; gap: 4px;',
    'white-space: nowrap; overflow: hidden;',
  ].join(' ')

  const listStyle = [
    'position: absolute; top: calc(100% + 3px); left: 0;',
    'background: #1e293b; border-radius: 5px; box-shadow: 0 4px 18px rgba(0,0,0,0.55);',
    'z-index: 100; display: none; max-height: 220px; overflow-y: auto; min-width: 100%;',
  ].join(' ')

  return html.div(
    attr.class(opts.wrapCls),
    attr.style('position: relative; display: inline-flex; flex-shrink: 0;'),
    on.click((e: MouseEvent) => e.stopPropagation()),

    // Trigger (div, not button — divs are not focusable so they never steal focus)
    html.div(
      attr.style(triggerStyle + ' cursor: pointer;'),
      on.mousedown((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // Close any OTHER open dropdown immediately (it's not under this click)
        if (activeDropdownList) {
          const other = activeDropdownList
          if (
            other.parentElement &&
            !other.parentElement.contains(e.target as Node)
          ) {
            other.style.display = 'none'
            activeDropdownList = null
          }
        }
        // Toggle this list — but do it in on.click so the list stays visible
        // until after mouseup/click have already resolved their targets.
      }),
      on.click((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        const listEl = document.querySelector<HTMLElement>(`.${opts.listCls}`)
        if (!listEl) return
        const isOpen = listEl.style.display !== 'none'
        listEl.style.display = isOpen ? 'none' : 'block'
        activeDropdownList = isOpen ? null : listEl
      }),
      opts.editable
        ? html.input(
            attr.class(opts.labelCls),
            attr.type('text'),
            Attr('value', opts.initialLabel),
            attr.style(
              'flex:1; width:0; min-width:0; border:none; background:transparent; color:#e2e8f0; font-size:15px; outline:none; padding:0;'
            ),
            on.mousedown((e: MouseEvent) => {
              e.stopPropagation()
              const listEl = document.querySelector<HTMLElement>(
                `.${opts.listCls}`
              )
              const listOpen = listEl && listEl.style.display !== 'none'
              if (!listOpen) {
                // First click: behave exactly like the caret — the bubbling
                // click opens the dropdown, and preventDefault keeps focus
                // (and the visible text selection) in the editing overlay.
                e.preventDefault()
                return
              }
              // Second click while the list is open: focus the input so a
              // custom value can be typed. Save the overlay selection first
              // and keep it visibly highlighted while the input has focus.
              const sel = window.getSelection()
              if (sel && sel.rangeCount > 0) {
                _toolbarSavedRange = sel.getRangeAt(0).cloneRange()
                setPreservedHighlight(_toolbarSavedRange)
              }
            }),
            on.focus((e: FocusEvent) => {
              ;(e.target as HTMLInputElement).select()
            }),
            on.input(() => {
              inputDirty = true
            }),
            on.keydown((e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                ;(e.target as HTMLInputElement).blur()
              }
              if (e.key === 'Escape') {
                inputDirty = false
                ;(e.target as HTMLInputElement).value = opts.initialLabel
                ;(e.target as HTMLInputElement).blur()
              }
            }),
            on.blur((e: FocusEvent) => {
              const val = (e.target as HTMLInputElement).value.trim()
              // Focus moving straight into the overlay means the user is
              // placing a new cursor/selection there — don't stomp it by
              // restoring the saved range.
              const intoOverlay = Boolean(
                (e.relatedTarget as HTMLElement | null)?.closest(
                  '.editing-overlay'
                )
              )
              if (!inputDirty || !val || !opts.onType) {
                // Nothing to apply — restore focus and the real selection
                requestAnimationFrame(() => {
                  const overlay =
                    document.querySelector<HTMLElement>('.editing-overlay')
                  if (overlay && !intoOverlay) {
                    overlay.focus()
                    if (_toolbarSavedRange) {
                      try {
                        const s = window.getSelection()
                        if (s) {
                          s.removeAllRanges()
                          s.addRange(_toolbarSavedRange)
                        }
                      } catch {
                        /* stale */
                      }
                    }
                  }
                  setPreservedHighlight(null)
                })
                return
              }
              // Defer until after blur completes so focus transfer works
              inputDirty = false
              const typeCb = opts.onType
              requestAnimationFrame(() => {
                const overlay =
                  document.querySelector<HTMLElement>('.editing-overlay')
                if (overlay) {
                  overlay.focus()
                  if (_toolbarSavedRange) {
                    try {
                      const s = window.getSelection()
                      if (s) {
                        s.removeAllRanges()
                        s.addRange(_toolbarSavedRange)
                      }
                    } catch {
                      /* stale */
                    }
                  }
                }
                typeCb(val)
                _toolbarSavedRange = null
                setPreservedHighlight(null)
              })
            })
          )
        : html.span(
            attr.class(opts.labelCls),
            attr.style(
              'flex: 1; overflow: hidden; text-overflow: ellipsis; text-align: left;'
            ),
            opts.initialLabel
          ),
      html.span(
        attr.style('font-size: 11px; color: #94a3b8; flex-shrink: 0;'),
        '▾'
      )
    ),

    // Options list
    html.div(
      attr.class(`${opts.listCls} toolbar-dd-list`),
      attr.style(listStyle),
      ...opts.options.map(opt =>
        html.div(
          attr.style(
            'padding: 6px 12px; cursor: pointer; font-size: 15px; color: #e2e8f0;' +
              'white-space: nowrap;'
          ),
          on.mouseenter((e: MouseEvent) => {
            ;(e.target as HTMLElement).style.background = '#334155'
          }),
          on.mouseleave((e: MouseEvent) => {
            ;(e.target as HTMLElement).style.background = ''
          }),
          on.mousedown((e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            // A pick always wins over half-typed input text.
            inputDirty = false
            opts.onSelect(opt.value)
            if (!opts.keepLabel) {
              const labelEl = document.querySelector<HTMLElement>(
                `.${opts.labelCls}`
              )
              if (labelEl) {
                if (opts.editable && labelEl instanceof HTMLInputElement)
                  labelEl.value = opt.label
                else labelEl.textContent = opt.label
              }
            }
            // Do NOT hide the list here. Hiding during mousedown causes mouseup to
            // fire on the canvas (now visible below), making click also target the
            // canvas and triggering its selectedId=null handler. Hide in on.click
            // instead, after the browser has already resolved the click target.
          }),
          on.click((e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const listEl = document.querySelector<HTMLElement>(
              `.${opts.listCls}`
            )
            if (listEl) listEl.style.display = 'none'
            activeDropdownList = null
          }),
          opt.label
        )
      )
    )
  )
}

export function renderFormattingToolbar(
  comp: EditableBlock,
  actions: DesignActions
) {
  // Rendered inside the element-controls stack (see PageCanvas), which
  // owns positioning AND, for text components, the shared dark shell
  // (background/radius/shadow) that makes the rows read as one panel.
  // Two rows (type controls; alignment + smart field) keep each row
  // narrow enough to clamp fully on-page even for components near the
  // right edge (see keepToolbarOnPage). Rows are transparent so
  // differing row widths blend into the shell instead of reading as
  // stacked pills.
  const rowStyle = (extra = '') =>
    [
      'display: flex; align-items: center; gap: 1px;',
      'background: transparent; padding: 4px 5px;',
      'user-select: none;',
      extra,
    ].join(' ')

  const baseBtn = (extra = '') =>
    [
      'padding: 0 11px; height: 34px; min-width: 37px;',
      'border: none; border-radius: 4px; cursor: pointer;',
      'font-size: 16px; font-family: inherit;',
      'background: transparent; color: #e2e8f0;',
      extra,
    ].join(' ')

  const sep = () =>
    html.div(
      attr.style(
        'width: 1px; height: 25px; background: #475569; margin: 0 4px; flex-shrink: 0;'
      )
    )

  function inlineBtn(label: string, cmd: string, cls: string, extraCss = '') {
    return html.button(
      attr.class(cls),
      attr.style(baseBtn(extraCss)),
      on.mousedown((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (TOGGLE_CMDS.has(cmd)) execToggleWithChips(cmd as ToggleCmd)
        else document.execCommand(cmd)
        // The command changes the DOM but not the selection, so no
        // selectionchange fires — refresh the active states explicitly.
        requestAnimationFrame(() => updateToolbarState())
      }),
      label
    )
  }

  function alignIconEl(type: 'left' | 'center' | 'right') {
    const justify =
      type === 'left' ? 'flex-start' : type === 'right' ? 'flex-end' : 'center'
    const widths =
      type === 'left'
        ? ['17px', '11px', '15px']
        : type === 'center'
          ? ['17px', '11px', '17px']
          : ['17px', '11px', '15px']
    const bar = (w: string) =>
      html.div(
        attr.style(
          `height: 2.5px; width: ${w}; background: currentColor; border-radius: 1px;`
        )
      )
    return html.div(
      attr.style(
        `display: flex; flex-direction: column; gap: 4px; align-items: ${justify}; pointer-events: none;`
      ),
      bar(widths[0]),
      bar(widths[1]),
      bar(widths[2])
    )
  }

  function alignBtn(
    cmd: string,
    type: 'left' | 'center' | 'right',
    cls: string
  ) {
    return html.button(
      attr.class(cls),
      attr.style(baseBtn()),
      on.mousedown((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // justify* refuses selections that offer it no editable content
        // (a chip-only line) — and its per-line block restructuring is
        // exactly the behavior we want, so rather than reimplementing it,
        // let the native command run with chips temporarily editable.
        // Alignment only moves blocks; it never rewrites inline content.
        const overlay = document.querySelector<HTMLElement>('.editing-overlay')
        const chips = Array.from(
          overlay?.querySelectorAll<HTMLElement>('[data-token]') ?? []
        )
        chips.forEach(c => c.setAttribute('contenteditable', 'true'))
        try {
          document.execCommand(cmd)
        } finally {
          chips.forEach(c => c.setAttribute('contenteditable', 'false'))
        }
        requestAnimationFrame(() => updateToolbarState())
      }),
      alignIconEl(type)
    )
  }

  const fontDropdown = ToolbarDropdown({
    wrapCls: 'toolbar-font-wrap',
    labelCls: 'toolbar-font-label',
    listCls: 'toolbar-font-list',
    initialLabel:
      FONT_OPTIONS.find(o => o.value === comp.fontFamily)?.label ?? 'Font',
    width: '138px',
    options: FONT_OPTIONS,
    onSelect: value => {
      const chips = chipsInSelection()
      document.execCommand('fontName', false, value)
      for (const chip of chips) {
        ensureChipWrapper(chip).style.fontFamily = value
      }
    },
  })

  function applyFontSize(px: number) {
    if (isNaN(px) || px < 1) return
    const overlay = document.querySelector<HTMLElement>('.editing-overlay')
    if (!overlay) return
    overlay.focus()
    if (_toolbarSavedRange) {
      try {
        const s = window.getSelection()
        if (s) {
          s.removeAllRanges()
          s.addRange(_toolbarSavedRange)
        }
      } catch {
        /* stale range */
      }
      _toolbarSavedRange = null
    }
    const chips = chipsInSelection()
    document.execCommand('fontSize', false, '7')
    overlay.querySelectorAll<HTMLElement>('font[size="7"]').forEach(el => {
      el.removeAttribute('size')
      el.style.fontSize = `${px}px`
    })
    for (const chip of chips) {
      ensureChipWrapper(chip).style.fontSize = `${px}px`
    }
    // The execCommand above fired a synchronous input event while the text
    // was transiently at size 7, which pushed the toolbar/handles down to
    // the inflated height — re-sync now that the real size is in place.
    syncOverlayChrome(comp)
  }

  const sizeDropdown = ToolbarDropdown({
    wrapCls: 'toolbar-size-wrap',
    labelCls: 'toolbar-size-label',
    listCls: 'toolbar-size-list',
    initialLabel: `${comp.fontSize}px`,
    width: '75px',
    editable: true,
    options: FONT_SIZES.map(sz => ({ label: `${sz}px`, value: String(sz) })),
    onSelect: value => applyFontSize(parseInt(value, 10)),
    onType: value => applyFontSize(parseInt(value.replace('px', ''), 10)),
  })

  // Line-height icon + custom dropdown in a single pill
  const lineHeightIcon = html.div(
    attr.style(
      'display: flex; align-items: center; gap: 2px; padding-left: 6px; pointer-events: none; flex-shrink: 0;'
    ),
    html.div(
      attr.style('color: #94a3b8; font-size: 14px; line-height: 1;'),
      '↕'
    ),
    html.div(
      attr.style('display: flex; flex-direction: column; gap: 3px;'),
      html.div(
        attr.style(
          'width: 12px; height: 2px; background: #94a3b8; border-radius: 1px;'
        )
      ),
      html.div(
        attr.style(
          'width: 12px; height: 2px; background: #94a3b8; border-radius: 1px;'
        )
      ),
      html.div(
        attr.style(
          'width: 12px; height: 2px; background: #94a3b8; border-radius: 1px;'
        )
      )
    )
  )

  function liveContent(fallback: string): string {
    const el = document.querySelector<HTMLElement>('.editing-overlay')
    return el ? chipsToTokens(el.innerHTML) : fallback
  }

  function applyLineHeight(val: number) {
    if (isNaN(val) || val <= 0) return
    const savedRange =
      _toolbarSavedRange ??
      ((window.getSelection()?.rangeCount ?? 0) > 0
        ? window.getSelection()!.getRangeAt(0).cloneRange()
        : null)
    _toolbarSavedRange = null
    // Fold in the live overlay HTML so typed-but-uncommitted edits survive
    // the re-render this update triggers.
    actions.updateComponent(
      { ...comp, content: liveContent(comp.content), lineHeight: val },
      true
    )
    requestAnimationFrame(() => {
      const newOverlay = document.querySelector<HTMLElement>('.editing-overlay')
      if (!newOverlay) return
      newOverlay.focus()
      if (savedRange) {
        try {
          const s = window.getSelection()
          if (s) {
            s.removeAllRanges()
            s.addRange(savedRange)
          }
        } catch {
          /* range may be stale if DOM changed */
        }
      }
    })
  }

  const lhDropdown = ToolbarDropdown({
    wrapCls: 'toolbar-lh-wrap',
    labelCls: 'toolbar-lh-label',
    listCls: 'toolbar-lh-list',
    initialLabel: String(comp.lineHeight),
    width: '65px',
    btnBg: 'transparent',
    editable: true,
    options: LINE_HEIGHTS.map(lh => ({ label: lh, value: lh })),
    onSelect: value => applyLineHeight(parseFloat(value)),
    onType: value => applyLineHeight(parseFloat(value)),
  })

  const lineHeightWrapper = html.div(
    attr.style(
      'display: flex; align-items: center; height: 34px; background: #334155; border-radius: 4px; overflow: visible;'
    ),
    lineHeightIcon,
    lhDropdown
  )

  // User feedback: "Insert field" reads like it inserts a value, so the
  // placeholder → Profile relationship went unnoticed. A "?" next to the
  // dropdown opens an explicit explanation.
  function insertFieldHelp() {
    const popCls = 'toolbar-field-help-pop'
    const popStyle = [
      'position: absolute; top: calc(100% + 6px); right: 0;',
      'width: 320px; padding: 14px 16px; z-index: 100; display: none;',
      'background: white; color: #374151; border: 1px solid #e5e7eb;',
      'border-radius: 8px; box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);',
      'font-size: 13px; line-height: 1.5; font-weight: 400;',
      'font-family: system-ui, -apple-system, sans-serif;',
      'white-space: normal; text-align: left; cursor: default;',
    ].join(' ')
    const para = (top: string) => `margin-top: ${top}; margin-bottom: 0;`
    const token = html.code(
      attr.style(
        'background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 3px; padding: 0 4px; font-size: 12px; color: #b45a2a;'
      ),
      '{{recruiter.name}}'
    )
    return html.div(
      attr.class('toolbar-field-help-wrap'),
      attr.style('position: relative; display: inline-flex; flex-shrink: 0;'),
      on.click((e: MouseEvent) => e.stopPropagation()),
      html.div(
        attr.class('toolbar-field-help-btn'),
        Attr('title', 'What does Smart field do?'),
        attr.style(
          [
            'height: 20px; width: 20px; border-radius: 50%; cursor: pointer;',
            'display: inline-flex; align-items: center; justify-content: center; align-self: center;',
            'background: #334155; color: #cbd5e1; font-size: 13px; font-weight: 700;',
          ].join(' ')
        ),
        // mousedown+preventDefault keeps focus (and the visible selection)
        // in the contenteditable overlay, same as every other toolbar control.
        on.mousedown((e: MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
        }),
        on.click((e: MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
          const pop = document.querySelector<HTMLElement>(`.${popCls}`)
          if (!pop) return
          if (activeDropdownList && activeDropdownList !== pop) {
            activeDropdownList.style.display = 'none'
            activeDropdownList = null
          }
          const isOpen = pop.style.display !== 'none'
          pop.style.display = isOpen ? 'none' : 'block'
          // Registering as the active dropdown gives us outside-click close
          // for free via the document-level mousedown handler.
          activeDropdownList = isOpen ? null : pop
        }),
        '?'
      ),
      html.div(
        attr.class(popCls),
        attr.style(popStyle),
        on.mousedown((e: MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
        }),
        html.div(
          attr.style(
            'font-weight: 700; font-size: 13px; color: #1e1e1e; margin-bottom: 6px;'
          ),
          'What “Smart field” does'
        ),
        html.p(
          attr.style(para('0')),
          'It inserts a ',
          html.b('placeholder'),
          ' into your text — like ',
          token,
          ' — not the actual value.'
        ),
        html.p(
          attr.style(para('8px')),
          'Whenever a resume is branded with this design, each placeholder is automatically replaced with the matching value from your ',
          html.b('Profile'),
          '.'
        ),
        html.p(
          attr.style(para('8px')),
          'To set those values: click ',
          html.b('your name in the upper right'),
          ', choose ',
          html.b('Profile'),
          ', and fill in the Recruiter and Agency details.'
        ),
        html.p(
          attr.style(para('8px') + ' color: #6b7280; font-size: 12px;'),
          'Recruiter fields use the details of whoever brands the resume, so one design shows the right contact info for every recruiter. Agency fields are shared by everyone at your agency.'
        ),
        html.button(
          attr.style(
            [
              'margin-top: 10px; padding: 5px 12px; cursor: pointer;',
              'background: var(--color-teal-600); color: white; border: none; border-radius: 4px;',
              'font-size: 12px; font-weight: 600;',
            ].join(' ')
          ),
          on.click((e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const pop = document.querySelector<HTMLElement>(`.${popCls}`)
            if (pop) pop.style.display = 'none'
            if (activeDropdownList?.classList.contains(popCls)) {
              activeDropdownList = null
            }
          }),
          'Got it'
        )
      )
    )
  }

  // Insert a dynamic field at the cursor, as an atomic chip. The trailing
  // zero-width space gives the caret a text position to land on after the
  // non-editable chip; chipsToTokens strips it on commit.
  const insertFieldDropdown = ToolbarDropdown({
    wrapCls: 'toolbar-field-wrap',
    labelCls: 'toolbar-field-label',
    listCls: 'toolbar-field-list',
    initialLabel: 'Smart field',
    width: '112px',
    options: INSERT_FIELD_OPTIONS,
    keepLabel: true,
    onSelect: token => {
      const chip = chipHtml(token)
      if (chip) document.execCommand('insertHTML', false, `${chip}\u200B`)
      else document.execCommand('insertText', false, token)
    },
  })

  return Fragment(
    html.div(
      attr.class('formatting-toolbar'),
      attr.style(rowStyle()),
      on.click((e: MouseEvent) => e.stopPropagation()),

      fontDropdown,
      sep(),
      sizeDropdown,
      lineHeightWrapper,
      sep(),

      inlineBtn('B', 'bold', 'toolbar-btn-bold', 'font-weight: bold;'),
      inlineBtn('I', 'italic', 'toolbar-btn-italic', 'font-style: italic;'),
      inlineBtn(
        'U',
        'underline',
        'toolbar-btn-underline',
        'text-decoration: underline;'
      ),
      inlineBtn(
        'S',
        'strikeThrough',
        'toolbar-btn-strike',
        'text-decoration: line-through;'
      ),

      colorPickerControl()
    ),
    html.div(
      attr.class('formatting-toolbar-row2'),
      attr.style(rowStyle('border-top: 1px solid #334155;')),
      on.click((e: MouseEvent) => e.stopPropagation()),

      alignBtn('justifyLeft', 'left', 'toolbar-btn-align-left'),
      alignBtn('justifyCenter', 'center', 'toolbar-btn-align-center'),
      alignBtn('justifyRight', 'right', 'toolbar-btn-align-right'),

      sep(),

      insertFieldDropdown,
      insertFieldHelp()
    )
  )
}

function colorPickerControl() {
  // ── Trigger: "A" with colored underline ──────────────────────
  const trigger = html.div(
    attr.style(
      [
        'height: 34px; min-width: 34px; padding: 0 7px; border-radius: 4px; cursor: pointer;',
        'display: inline-flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;',
        'color: #e2e8f0;',
      ].join(' ')
    ),
    on.mousedown((e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (activeDropdownList) {
        activeDropdownList.style.display = 'none'
        activeDropdownList = null
      }
    }),
    on.click((e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const panel = document.querySelector<HTMLElement>('.toolbar-cp-panel')
      if (!panel) return
      const isOpen = panel.style.display !== 'none'
      if (!isOpen) {
        // Initialise picker from current selection colour
        const overlay = document.querySelector<HTMLElement>('.editing-overlay')
        if (overlay) {
          const sel = window.getSelection()
          if (sel && sel.rangeCount > 0) {
            _cpSavedRange = sel.getRangeAt(0).cloneRange()
            let node: Node = sel.getRangeAt(0).startContainer
            if (node.nodeType === Node.TEXT_NODE) node = node.parentElement!
            if (overlay.contains(node)) {
              const rgb = window.getComputedStyle(node as HTMLElement).color
              const m = rgb.match(/[\d.]+/g)
              if (m && m.length >= 3) {
                const toHex = (n: number) =>
                  Math.round(+n).toString(16).padStart(2, '0')
                const hsv = hexToHsv(
                  `#${toHex(+m[0])}${toHex(+m[1])}${toHex(+m[2])}`
                )
                _cpHue = hsv.h
                _cpSat = hsv.s
                _cpVal = hsv.v
                // rgba() computed colors carry the current alpha; plain
                // rgb() means fully opaque.
                _cpOpacity =
                  m.length >= 4
                    ? Math.round(
                        Math.max(0, Math.min(1, parseFloat(m[3]))) * 100
                      )
                    : 100
              }
            }
          }
        }
        cpSyncUI()
      }
      panel.style.display = isOpen ? 'none' : 'flex'
      activeDropdownList = isOpen ? null : panel
    }),
    html.span(
      attr.style(
        'font-size: 16px; font-weight: bold; line-height: 1; pointer-events: none;'
      ),
      'A'
    ),
    html.div(
      attr.class('toolbar-color-indicator'),
      attr.style(
        'width: 17px; height: 4px; background: #000000; border-radius: 1px; pointer-events: none;'
      )
    )
  )

  // ── Sat/Val square ────────────────────────────────────────────
  const colorSquare = html.div(
    attr.class('toolbar-cp-sq'),
    attr.style(
      [
        'position: relative; width: 180px; height: 130px; border-radius: 4px; cursor: crosshair; flex-shrink: 0;',
        `background: linear-gradient(to bottom, rgba(0,0,0,0), #000), linear-gradient(to right, #fff, hsl(${_cpHue},100%,50%));`,
      ].join(' ')
    ),
    on.mousedown((e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
      _cpSat = Math.max(
        0,
        Math.min(100, ((e.clientX - r.left) / r.width) * 100)
      )
      _cpVal = Math.max(
        0,
        Math.min(100, 100 - ((e.clientY - r.top) / r.height) * 100)
      )
      _cpDragging = 'sq'
      cpSyncUI()
      cpApplyColor()
    }),
    // Crosshair thumb
    html.div(
      attr.class('toolbar-cp-sq-thumb'),
      attr.style(
        [
          'position: absolute; width: 12px; height: 12px; border-radius: 50%; pointer-events: none;',
          'border: 2px solid #fff; box-shadow: 0 0 0 1.5px rgba(0,0,0,0.6);',
          'transform: translate(-50%, -50%);',
          `left: ${_cpSat}%; top: ${100 - _cpVal}%;`,
        ].join(' ')
      )
    )
  )

  // ── Hue bar ───────────────────────────────────────────────────
  const hueBar = html.div(
    attr.style('position: relative; padding: 3px 0; overflow: visible;'),
    html.div(
      attr.class('toolbar-cp-hue'),
      attr.style(
        [
          'position: relative; height: 14px; border-radius: 7px; cursor: ew-resize; overflow: visible;',
          'background: linear-gradient(to right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00);',
        ].join(' ')
      ),
      on.mousedown((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
        _cpHue = Math.max(
          0,
          Math.min(360, ((e.clientX - r.left) / r.width) * 360)
        )
        _cpDragging = 'hue'
        cpSyncUI()
        cpApplyColor()
      }),
      // Hue thumb
      html.div(
        attr.class('toolbar-cp-hue-thumb'),
        attr.style(
          [
            'position: absolute; top: -3px; width: 8px; height: 20px; border-radius: 4px;',
            'background: #fff; box-shadow: 0 0 0 1.5px rgba(0,0,0,0.55);',
            'transform: translateX(-50%); pointer-events: none;',
            `left: ${(_cpHue / 360) * 100}%;`,
          ].join(' ')
        )
      )
    )
  )

  // ── Opacity slider ────────────────────────────────────────────
  const opacityRow = html.div(
    attr.style('display: flex; align-items: center; gap: 6px;'),
    html.span(
      attr.style('font-size: 10px; color: #94a3b8; flex-shrink: 0;'),
      'Opacity'
    ),
    html.input(
      attr.class('toolbar-cp-opacity'),
      attr.type('range'),
      Attr('min', '0'),
      Attr('max', '100'),
      Attr('value', String(Math.round(_cpOpacity))),
      attr.style(
        'flex: 1; min-width: 0; height: 6px; accent-color: #3b82f6; cursor: pointer;'
      ),
      // Native slider drag needs the default mousedown behavior; just keep
      // the event from bubbling into canvas selection handlers.
      on.mousedown((e: MouseEvent) => e.stopPropagation()),
      on.input((e: Event) => {
        _cpOpacity = parseInt((e.target as HTMLInputElement).value, 10) || 0
        cpSyncUI()
      }),
      // Apply on release, not per-tick: cpApplyColor refocuses the editing
      // overlay, which would fight the slider mid-drag.
      on.change(() => cpApplyColor())
    ),
    html.input(
      attr.class('toolbar-cp-opacity-label'),
      attr.type('text'),
      Attr('maxlength', '3'),
      Attr('value', String(Math.round(_cpOpacity))),
      attr.style(
        [
          'width: 30px; height: 22px; padding: 0 3px; flex-shrink: 0;',
          'text-align: right; background: #0f172a; color: #e2e8f0;',
          'border: 1px solid #475569; border-radius: 4px;',
          'font-size: 12px; outline: none;',
        ].join(' ')
      ),
      on.mousedown((e: MouseEvent) => e.stopPropagation()),
      on.keydown((e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          // preventDefault: blur() below synchronously refocuses the
          // editing overlay (via cpApplyColor), and this same keystroke's
          // default action would otherwise insert a newline there, eating
          // the restored selection.
          e.preventDefault()
          ;(e.target as HTMLInputElement).blur()
        }
      }),
      on.input((e: Event) => {
        const v = parseInt((e.target as HTMLInputElement).value, 10)
        if (isNaN(v)) return
        _cpOpacity = Math.max(0, Math.min(100, v))
        cpSyncUI()
      }),
      // Apply on commit, not per keystroke: cpApplyColor refocuses the
      // editing overlay, which would fight the typing.
      on.blur(() => cpApplyColor())
    ),
    html.span(
      attr.style('font-size: 12px; color: #94a3b8; flex-shrink: 0;'),
      '%'
    )
  )

  // ── Hex input + Apply + preview ───────────────────────────────
  const hexRow = html.div(
    attr.style(
      'display: flex; align-items: center; gap: 6px; margin-top: 2px;'
    ),

    // Preview swatch
    html.div(
      attr.class('toolbar-cp-preview'),
      attr.style(
        [
          'width: 26px; height: 26px; border-radius: 4px; flex-shrink: 0;',
          `background: ${hsvToHex(_cpHue, _cpSat, _cpVal)}; border: 1px solid rgba(255,255,255,0.15);`,
        ].join(' ')
      )
    ),

    // "#" label
    html.span(
      attr.style('color: #64748b; font-size: 12px; flex-shrink: 0;'),
      '#'
    ),

    // Hex text input
    html.input(
      attr.class('toolbar-cp-hex'),
      attr.type('text'),
      Attr('value', hsvToHex(_cpHue, _cpSat, _cpVal).slice(1).toUpperCase()),
      Attr('maxlength', '6'),
      Attr('spellcheck', 'false'),
      attr.style(
        [
          'flex: 1; min-width: 0; height: 26px; padding: 0 6px;',
          'border-radius: 4px; border: 1px solid #475569;',
          'background: #0f172a; color: #e2e8f0; font-size: 12px; font-family: monospace;',
          'outline: none;',
        ].join(' ')
      ),
      on.input((e: Event) => {
        const val = (e.target as HTMLInputElement).value
        if (/^[0-9a-fA-F]{6}$/.test(val)) {
          const hsv = hexToHsv('#' + val)
          _cpHue = hsv.h
          _cpSat = hsv.s
          _cpVal = hsv.v
          cpSyncUI()
        }
      })
    ),

    // Apply button
    html.div(
      attr.style(
        [
          'height: 26px; padding: 0 8px; border-radius: 4px; cursor: pointer; flex-shrink: 0;',
          'background: #3b82f6; color: #fff; font-size: 12px;',
          'display: flex; align-items: center;',
        ].join(' ')
      ),
      on.mousedown((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // Parse hex input in case user typed a value
        const hexInput =
          document.querySelector<HTMLInputElement>('.toolbar-cp-hex')
        const val = hexInput?.value ?? ''
        if (/^[0-9a-fA-F]{6}$/.test(val)) {
          const hsv = hexToHsv('#' + val)
          _cpHue = hsv.h
          _cpSat = hsv.s
          _cpVal = hsv.v
          cpSyncUI()
        }
        cpApplyColor()
      }),
      on.click((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
      }),
      'Apply'
    )
  )

  // ── Panel ─────────────────────────────────────────────────────
  const panel = html.div(
    attr.class('toolbar-cp-panel'),
    attr.style(
      [
        'position: absolute; top: calc(100% + 3px); left: 0;',
        'background: #1e293b; border-radius: 6px; box-shadow: 0 4px 20px rgba(0,0,0,0.6);',
        'z-index: 100; padding: 8px; width: 196px; box-sizing: border-box;',
        'flex-direction: column; gap: 8px;', // display set imperatively (starts hidden via rAF)
      ].join(' ')
    ),
    on.mousedown((e: MouseEvent) => e.stopPropagation()),
    on.click((e: MouseEvent) => e.stopPropagation()),
    colorSquare,
    hueBar,
    opacityRow,
    hexRow
  )

  // Panel starts hidden; set imperatively after mount so the style attr doesn't conflict
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>('.toolbar-cp-panel')
    if (el) el.style.display = 'none'
  })

  return html.div(
    attr.style('position: relative; display: inline-flex; flex-shrink: 0;'),
    on.click((e: MouseEvent) => e.stopPropagation()),
    trigger,
    panel
  )
}
